import { Alert, Stack, Tooltip } from "@mui/material"
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid"
import { ReactElement, useMemo } from "react"

import AdminDataGrid from "../../../components/dataDisplay/adminDataGrid/adminDataGrid"
import DataLoadError from "../../../components/fallback/dataLoadError"
import useGetCurrentConfig from "../../../services/middleware/hooks/dnoThreshold/useGetCurrentConfig"
import useGetLicenceAreaThresholds from "../../../services/middleware/hooks/dnoThreshold/useGetLicenceAreaThresholds"
import {
    IDnoConfigItem,
    IThresholdItem,
} from "../../../services/middleware/types/dnoThreshold/dnoThresholdTypes"
import {
    getDisplayName,
    getFieldDescription,
    getFieldCategory,
} from "../../../utils/thresholdDictionary/thresholdDictionary"

/**
 * Renders a field name cell with the display name and
 * optional tooltip showing the description on hover.
 * Expects valueGetter to have already resolved the display name
 * into `value`, and reads the raw API name from `row.name`
 * for the description lookup.
 */
const FieldNameCell = ({ value, row }: GridRenderCellParams): ReactElement => {
    const display = String(value)
    const description = getFieldDescription(String(row.name))
    return description ? (
        <Tooltip title={description} arrow>
            <span>{display}</span>
        </Tooltip>
    ) : (
        <span>{display}</span>
    )
}

const CONFIG_COLUMNS: GridColDef[] = [
    {
        field: "category",
        headerName: "Category",
        flex: 1,
        valueGetter: (_value: unknown, row: IDnoConfigItem): string =>
            getFieldCategory(row.name),
    },
    {
        field: "name",
        headerName: "DNO Config Field",
        flex: 1,
        valueGetter: (_value: unknown, row: IDnoConfigItem): string =>
            getDisplayName(row.name),
        renderCell: FieldNameCell,
    },
    {
        field: "value",
        headerName: "Current Value",
        flex: 1,
        valueFormatter: (value: unknown): string => String(value ?? "—"),
    },
]

/** Flat row for display — adds thresholdId from the dict key */
type ThresholdRow = IThresholdItem & { thresholdId: string }

const THRESHOLD_COLUMNS: GridColDef[] = [
    { field: "thresholdId", headerName: "Licence Area", flex: 1 },
    {
        field: "category",
        headerName: "Category",
        flex: 1,
        valueGetter: (_value: unknown, row: ThresholdRow): string =>
            getFieldCategory(row.name),
    },
    {
        field: "name",
        headerName: "Threshold",
        flex: 2,
        valueGetter: (_value: unknown, row: ThresholdRow): string =>
            getDisplayName(row.name),
        renderCell: FieldNameCell,
    },
    {
        field: "value",
        headerName: "Current Value",
        flex: 1,
        valueFormatter: (value: unknown): string => String(value ?? "—"),
    },
]

/**
 * Tab 1: Current Configuration — read-only view of DNO config
 * and licence area thresholds using AdminDataGrid.
 */
const CurrentConfiguration = ({
    isThresholdManager,
}: {
    isThresholdManager: boolean
}): ReactElement => {
    const {
        data: configData,
        error: configError,
        isLoading: configLoading,
    } = useGetCurrentConfig()
    const {
        data: thresholdData,
        error: thresholdError,
        isLoading: thresholdLoading,
    } = useGetLicenceAreaThresholds()

    const filteredConfig = useMemo(() => configData?.config ?? [], [configData])

    const flatThresholds: ThresholdRow[] = useMemo(
        () =>
            Object.entries(thresholdData?.thresholds ?? {}).flatMap(
                ([thresholdId, items]) =>
                    items.map((t) => ({ ...t, thresholdId }))
            ),
        [thresholdData]
    )

    if (configError || thresholdError) return <DataLoadError />

    return (
        <Stack spacing={2}>
            {isThresholdManager ? (
                <Alert severity="info" variant="outlined">
                    This is your current live configuration. These values are
                    applied to all connection applications assessed for your
                    DNO. To make changes, switch to the Configuration Changes
                    tab.
                </Alert>
            ) : (
                <Alert severity="info" variant="outlined">
                    This is the current live configuration for your DNO. If you
                    have any questions about these values, please contact your
                    Threshold Manager.
                </Alert>
            )}
            <AdminDataGrid<IDnoConfigItem>
                rows={filteredConfig}
                columns={CONFIG_COLUMNS}
                getRowId={(row): string => row.name}
                itemTypeText="DNO Configurations"
                isLoading={configLoading}
                showActions={false}
                toolbarButtons={["reorder", "filter", "export"]}
                initialPageSize={25}
                initialSortField="category"
                showTitle
                disableRowSelectionOnClick
            />
            <AdminDataGrid<ThresholdRow>
                rows={flatThresholds}
                columns={THRESHOLD_COLUMNS}
                getRowId={(row): string => `${row.thresholdId}-${row.name}`}
                itemTypeText="Thresholds by Licence Area"
                isLoading={thresholdLoading}
                showActions={false}
                toolbarButtons={["reorder", "filter", "export"]}
                initialPageSize={25}
                initialSortField="thresholdId"
                showTitle
                disableRowSelectionOnClick
            />
        </Stack>
    )
}

export default CurrentConfiguration
